import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getEmployerDocuments } from "../../api/document/getEmployerDocuments";
import { getMyDossiers } from "../../api/dossier/getMyDossiers";
import { getMyEmployerSummaryStats } from "../../api/dossier/getMyEmployerSummaryStats";
import { getMyEmployerVehicleSettlementSurveyResponses } from "../../api/dossier/getMyEmployerVehicleSettlementSurveyResponses";
import { qualifyNewLead } from "../../api/user/qualifyNewLead";
import APButton from "../../components/buttons/APButton";
import { APButtonLink } from "../../components/buttons/APButtonLink";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import MenuCard from "../../components/MenuCard";
import PageContainer from "../../components/PageContainer";
import { shuffleArray } from "../../helpers/array";
import {
  filterAccountsByAnalyticsPermission,
  filterAccountsByPermission,
  hasPermission,
  isAuthorisedToViewEmployerPage,
} from "../../helpers/user";
import { useFaqs } from "../../hooks/useFaqs";
import { useShowPayrollTaxSavings } from "../../hooks/useShowPayrollTaxSavings";
import { useAuth } from "../../providers/AuthProvider";
import { User } from "../../types/User";
import { DEFAULT_PAGE_SIZE } from "../components/DocumentList";
import EmployerHomeInsights from "./components/EmployerHomeInsights";
import VehicleSettlementSurveyResponseCarousel from "./components/VehicleSettlementSurveyResponseCarousel";

const EmployerHome = ({ me }: { me: User }) => {
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();
  const [navigationCheckComplete, setNavigationCheck] = useState(false);
  const promotionAuthorisedAccounts = me.accountPermissions
    ? filterAccountsByPermission(me.accountPermissions, "viewPromotions")
    : [];

  const { mutateAsync, isLoading: isUpdating } = qualifyNewLead();

  const handleNewLead = async () => {
    await mutateAsync();
    navigate(`${getScopedPageUrl("viewDossiers")}?openFirst=true`);
  };

  const accountsWithAnalytics = me.accountPermissions
    ? filterAccountsByAnalyticsPermission(me.accountPermissions)
    : [];

  const promotionDocuments = getEmployerDocuments(
    {
      pageSize: DEFAULT_PAGE_SIZE,
      accountIds: promotionAuthorisedAccounts.map((account) => account.id),
      documentTypes: ["Promotion"],
    },
    { disabled: !navigationCheckComplete }
  );
  const dossiers = getMyDossiers();
  const vehicleSettlementSurveyResponses =
    getMyEmployerVehicleSettlementSurveyResponses({
      pageSize: 100,
    });
  const employerSummaryStats = getMyEmployerSummaryStats({
    disabled: accountsWithAnalytics.length === 0,
  });
  const payrollTaxSavings = useShowPayrollTaxSavings(
    employerSummaryStats.data?.payrollTaxSavings.totalPayrollTaxSavings
  );

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "employer-home",
  });

  if (dossiers.isError) {
    throw dossiers.error;
  }

  if (vehicleSettlementSurveyResponses.isError) {
    throw vehicleSettlementSurveyResponses.error;
  }

  if (payrollTaxSavings.isError) {
    throw payrollTaxSavings.error;
  }

  useEffect(() => {
    if (
      me.accountPermissions &&
      hasPermission(me.accountPermissions, "accessEmployer")
    ) {
      setNavigationCheck(true);
    } else {
      navigate(getScopedPageUrl("viewDossiers"), { replace: true });
    }
  });

  const isLoading =
    dossiers.isLoading ||
    promotionDocuments.isLoading ||
    vehicleSettlementSurveyResponses.isLoading ||
    (accountsWithAnalytics.length > 0 && employerSummaryStats.isLoading) ||
    isLoadingFaqs ||
    payrollTaxSavings.isLoading;
  const isSuccess =
    dossiers.isSuccess &&
    promotionDocuments.isFetched &&
    vehicleSettlementSurveyResponses.isSuccess &&
    (accountsWithAnalytics.length === 0 || employerSummaryStats.isSuccess) &&
    isSuccessFaqs &&
    payrollTaxSavings.isSuccess;

  const shuffledSurveyResponses = useMemo(
    () =>
      vehicleSettlementSurveyResponses.data?.items
        ? shuffleArray(vehicleSettlementSurveyResponses.data.items)
        : [],
    [vehicleSettlementSurveyResponses.data]
  );

  const accountsWithSurveyAccess = me.accountPermissions
    ? filterAccountsByPermission(
        me.accountPermissions,
        "viewVehicleSettlementSurveys"
      )
    : [];

  return (
    <PageContainer
      title=" "
      loading={isLoading || isUpdating || !navigationCheckComplete}
      titleButton={
        isSuccess ? (
          <Box>
            {dossiers.data.length > 0 && (
              <APButton
                size="large"
                color="primary"
                variant="contained"
                onClick={() => navigate(getScopedPageUrl("viewDossiers"))}
                text="My Car"
                icon="car"
              />
            )}
            {dossiers.data.length === 0 && (
              <APButton
                text="My New Car"
                onClick={handleNewLead}
                variant="contained"
                size="large"
                icon="car"
                color="primary"
              />
            )}
          </Box>
        ) : undefined
      }
    >
      {isSuccess && (
        <Stack spacing={2}>
          {accountsWithAnalytics.length > 0 &&
            employerSummaryStats.data &&
            employerSummaryStats.data.activeLeases.count > 0 && (
              <EmployerHomeInsights
                stats={employerSummaryStats.data}
                showPayrollTaxStats={payrollTaxSavings.showPayrollTaxStats}
              />
            )}

          {shuffledSurveyResponses.length > 0 && (
            <MenuCard
              title=""
              cardBody={
                <Box sx={{ width: "100%", position: "relative" }}>
                  <VehicleSettlementSurveyResponseCarousel
                    responses={shuffledSurveyResponses}
                    displayAccounts={accountsWithSurveyAccess.length > 1}
                  />
                </Box>
              }
            />
          )}

          {isAuthorisedToViewEmployerPage(
            me.accountPermissions || {},
            "employerApproveDrivers"
          ) && (
            <MenuCard
              title="Approve"
              primaryButton={
                <APButton
                  text="Approve Employees"
                  onClick={() =>
                    navigate(getScopedPageUrl("employerApproveDrivers"))
                  }
                  variant="contained"
                  size="large"
                />
              }
              secondaryButton={
                isAuthorisedToViewEmployerPage(
                  me.accountPermissions || {},
                  "employerPackagingAgreements"
                ) && (
                  <APButton
                    text="Packaging Agreements"
                    onClick={() =>
                      navigate(getScopedPageUrl("employerPackagingAgreements"))
                    }
                    variant="contained"
                    size="large"
                  />
                )
              }
            />
          )}

          <MenuCard
            title="Engagement"
            primaryButton={
              promotionDocuments.isSuccess &&
              promotionDocuments.data.count > 0 && (
                <APButton
                  text="Promotions"
                  onClick={() =>
                    navigate(getScopedPageUrl("employerPromotions"))
                  }
                  variant="contained"
                  size="large"
                />
              )
            }
            secondaryButton={
              isAuthorisedToViewEmployerPage(
                me.accountPermissions || {},
                "employerEngagementReports"
              ) && (
                <APButton
                  text="Reports"
                  onClick={() =>
                    navigate(getScopedPageUrl("employerEngagementReports"))
                  }
                  variant="contained"
                  size="large"
                />
              )
            }
          />

          {isAuthorisedToViewEmployerPage(
            me.accountPermissions || {},
            "employerInvoices"
          ) && (
            <MenuCard
              title="Invoices"
              primaryButton={
                <APButton
                  text="Invoices"
                  onClick={() => navigate(getScopedPageUrl("employerInvoices"))}
                  variant="contained"
                  size="large"
                />
              }
            />
          )}

          {isAuthorisedToViewEmployerPage(
            me.accountPermissions || {},
            "employerPayrollAdvice"
          ) && (
            <MenuCard
              title="Payroll"
              primaryButton={
                <APButton
                  text="Advices"
                  onClick={() =>
                    navigate(getScopedPageUrl("employerPayrollAdvice"))
                  }
                  variant="contained"
                  size="large"
                />
              }
            />
          )}

          {isAuthorisedToViewEmployerPage(
            me.accountPermissions || {},
            "employerFBTReports"
          ) && (
            <MenuCard
              title="FBT"
              primaryButton={
                <APButton
                  text="Reports"
                  onClick={() =>
                    navigate(getScopedPageUrl("employerFBTReports"))
                  }
                  variant="contained"
                  size="large"
                />
              }
            />
          )}

          {isAuthorisedToViewEmployerPage(
            me.accountPermissions || {},
            "employerViewDossiers"
          ) && (
            <MenuCard
              title="Leases"
              primaryButton={
                <APButton
                  text="Active"
                  onClick={() =>
                    navigate(
                      `${getScopedPageUrl(
                        "employerViewDossiers"
                      )}?status=Active`
                    )
                  }
                  variant="contained"
                  size="large"
                />
              }
              secondaryButton={
                hasPermission(
                  me.accountPermissions || {},
                  "terminateDossiers"
                ) && (
                  <APButton
                    text="Terminate"
                    onClick={() =>
                      navigate(
                        `${getScopedPageUrl(
                          "employerViewDossiers"
                        )}?status=Active`
                      )
                    }
                    variant="contained"
                    size="large"
                  />
                )
              }
              tertiaryButton={
                <Stack
                  spacing={2}
                  sx={{ display: "flex", alignItems: "flex-end" }}
                >
                  <Box>
                    <APButton
                      text="Termination in Progress"
                      onClick={() =>
                        navigate(
                          `${getScopedPageUrl(
                            "employerViewDossiers"
                          )}?status=Closing`
                        )
                      }
                      variant="contained"
                      size="large"
                    />
                  </Box>
                  <Box>
                    <APButton
                      text="Terminated"
                      onClick={() =>
                        navigate(
                          `${getScopedPageUrl(
                            "employerViewDossiers"
                          )}?status=Closed`
                        )
                      }
                      variant="contained"
                      size="large"
                    />
                  </Box>
                </Stack>
              }
            />
          )}

          {isAuthorisedToViewEmployerPage(
            me.accountPermissions || {},
            "employerLuxuryLeaseReports"
          ) && (
            <MenuCard
              title="Luxury"
              primaryButton={
                <APButton
                  text="Reports"
                  onClick={() =>
                    navigate(getScopedPageUrl("employerLuxuryLeaseReports"))
                  }
                  variant="contained"
                  size="large"
                />
              }
            />
          )}

          {payrollTaxSavings.showPayrollTaxStats && (
            <>
              {faqProps.renderAppLocation(
                "EmployerHome-PayrollTaxSavingsCalculation",
                <Box>
                  <APButtonLink
                    text="^ Payroll Tax Savings Calculation"
                    onClick={() => {}}
                  />
                </Box>,
                true
              )}
            </>
          )}
          <Box pt={3}>
            <APContactHelpButton />
          </Box>
        </Stack>
      )}
    </PageContainer>
  );
};

export default EmployerHome;
